import React, { useState, useEffect, useCallback } from 'react';
import useServices from '../../hooks/useServices';
import './Hero.css';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [progressWidth, setProgressWidth] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const { services, loading } = useServices();

  // Use services from API, fallback to empty array
  const slides = services.length > 0 ? services : [];
  const totalSlides = slides.length || 1;

  const nextSlide = useCallback(() => {
    if (isTransitioning || totalSlides <= 1) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev + 1) % totalSlides);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning, totalSlides]);

  const prevSlide = useCallback(() => {
    if (isTransitioning || totalSlides <= 1) return;
    setIsTransitioning(true);
    setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning, totalSlides]);

  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentSlide) return;
    setIsTransitioning(true);
    setCurrentSlide(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning, currentSlide]);

  useEffect(() => {
    if (isPaused || totalSlides <= 1) return;
    const slideInterval = setInterval(nextSlide, 5000);
    return () => clearInterval(slideInterval);
  }, [nextSlide, isPaused, totalSlides]);

  useEffect(() => {
    setProgressWidth(0);
    if (isPaused || totalSlides <= 1) return;
    const progressInterval = setInterval(() => {
      setProgressWidth((prev) => {
        if (prev >= 100) return 100;
        return prev + 1;
      });
    }, 50);
    return () => clearInterval(progressInterval);
  }, [currentSlide, isPaused, totalSlides]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide]);

  if (loading) {
    return (
      <section className="hero" id="hero">
        <div className="hero-slides">
          <div className="hero-slide active" style={{ background: '#1a1a2e' }} />
        </div>
      </section>
    );
  }

  // Fallback image if service has no image
  const fallbackImage = 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1920&q=80';

  return (
    <section
      className="hero"
      id="hero"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="hero-slides">
        {slides.map((service, index) => (
          <div
            key={service.id || index}
            className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
            style={{ backgroundImage: `url(${service.image || fallbackImage})` }}
          />
        ))}
        <div className="slide-overlay"></div>
      </div>

      {slides.length > 0 && (
        <div className="overlap-container">
          <div className="corner-accent"></div>
          <div className="content-wrapper">
            {slides.map((service, index) => (
              <div
                key={service.id || index}
                className={`text-group ${index === currentSlide ? 'active' : ''}`}
              >
                <h1>{service.name}</h1>
                <p>{service.description}</p>
              </div>
            ))}
          </div>
          <div className="button-wrapper">
            <a href="tel:0796049254" className="overlap-btn active">
              {slides[currentSlide]?.buttonText || 'Learn More'}
            </a>
          </div>
        </div>
      )}

      {slides.length > 1 && (
        <>
          <div className="carousel-dots">
            {slides.map((_, index) => (
              <span
                key={index}
                className={`dot ${index === currentSlide ? 'active' : ''}`}
                onClick={() => goToSlide(index)}
              />
            ))}
          </div>
          <div className="progress-bar" style={{ width: `${progressWidth}%` }}></div>
        </>
      )}
    </section>
  );
};

export default Hero;
